import {useEffect, useMemo, useState} from 'react';
import sessionStore from 'client/Session/SessionStore';
import {getSchoolsOfAdmin, schoolHasProUpgrade} from 'lib/hooks/useHasProUpgrade';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

import {useAdminContentLockGracePeriodsQuery} from './contentLockGracePeriods.queries.generated';
import {CONTENT_RESTRICTIONS_PHASE_3_FLAG} from './manageRestrictionsModal.constants';

export interface AdminSchool {
  id: string;
  name: string;
  /** Whether the school holds an active Pro license — content restriction requires Pro. */
  hasPro: boolean;
  /**
   * When the school's content locks come off unless pro is restored first — the end date of the
   * school's running `contentLockGracePeriods` row (#20975). Absent when the school isn't in a
   * grace window.
   */
  restrictionRemovalDate?: string;
}

interface UseAdminSchoolsResult {
  schools: AdminSchool[];
  loading: boolean;
  error: Error | undefined;
}

/**
 * The schools where the current user holds an approved administrator (non-teacher) role.
 *
 * `enabled` gates the fetch: the underlying `schools_v5` query is admin-only and 403s for teachers,
 * so callers that already know the viewer isn't an admin (e.g. the teacher-facing RestrictionIndicator)
 * pass `enabled={false}` to skip the request entirely instead of issuing one guaranteed to fail.
 */
export function useAdminSchools(enabled = true): UseAdminSchoolsResult {
  const [schools, setSchools] = useState<AdminSchool[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | undefined>();

  // The read policy scopes rows to the viewer's administered schools, so no filter is needed. A
  // failed read degrades to no removal warnings rather than blocking the modal — the toggles and
  // locks are still correct without it. The phase-3 flag is the single choke point for the whole
  // grace layer: skipped here, no school ever carries restrictionRemovalDate, so every surface
  // that feeds on it ships dark.
  const {isFeatureEnabled: phase3Enabled} = useIsFeatureFlagEnabled(
    CONTENT_RESTRICTIONS_PHASE_3_FLAG
  );
  const {data: gracePeriodData, loading: gracePeriodsLoading} =
    useAdminContentLockGracePeriodsQuery({skip: !enabled || !phase3Enabled});
  const removalDateBySchoolId = useMemo(() => {
    const bySchool = new Map<string, string>();
    for (const row of gracePeriodData?.contentLockGracePeriods?.results ?? []) {
      bySchool.set(row.schoolId, row.endDate);
    }
    return bySchool;
  }, [gracePeriodData]);

  useEffect(() => {
    if (!enabled) {
      // Not an admin / feature off: skip the admin-only schools_v5 query (it 403s for teachers). The
      // initial state is already empty and the consumer gates its output on `enabled`, so there's no
      // stale state to clear — just resolve loading.
      // oxlint-disable-next-line react/react-compiler -- effect intentionally kicks off a data fetch; the synchronous setState publishes its loading/reset state
      setLoading(false);
      return undefined;
    }

    let subscribed = true;
    setLoading(true);

    getSchoolsOfAdmin(sessionStore.getUserId())
      .then((schoolList) => {
        if (!subscribed) {
          return;
        }
        // Dynamic JSON:API mandark models; narrow to the getters used here. `schoolHasProUpgrade`
        // reads the included school_licenses_v1.upgrades_v1 off the raw resource.
        const mapped = (
          schoolList.toArray() as Array<{getId: () => string; getName: () => string}>
        ).map((school) => ({
          id: school.getId(),
          name: school.getName(),
          hasPro: schoolHasProUpgrade(school)
        }));
        setSchools(mapped);
        setLoading(false);
      })
      .catch((e) => {
        if (!subscribed) {
          return;
        }
        const fetchError = e as Error;
        logger.error('Failed to fetch admin schools: %o', fetchError);
        setError(fetchError);
        setLoading(false);
      });

    return () => {
      subscribed = false;
    };
  }, [enabled]);

  // Stamp removal dates at read time so consumers see one coherent school shape.
  const schoolsWithRemovalDates = useMemo(
    () =>
      schools.map((school) => {
        const removalDate = removalDateBySchoolId.get(school.id);
        return removalDate ? {...school, restrictionRemovalDate: removalDate} : school;
      }),
    [schools, removalDateBySchoolId]
  );

  return {
    schools: schoolsWithRemovalDates,
    loading: loading || (enabled && phase3Enabled && gracePeriodsLoading),
    error
  };
}
